/**
 * Bambu Filament Selector
 * Cascading Family -> Product -> Colour -> Spool picker.
 * Reads embedded JSON (rendered server-side by the section's Liquid), no API calls
 * except the standard Shopify AJAX Cart endpoint on Add to cart.
 *
 * TWO MODES, set by the section setting and read off #bfs-root's data-bfs-mode:
 *
 *   ready     Anything out of stock is HIDDEN — series, colours and spool types alike.
 *             The customer only ever sees what can ship today.
 *
 *   preorder  The full catalogue, priced at 85%. Nothing is hidden. A twin variant
 *             seeded to 0 is not orderable and reads "Currently not available for
 *             pre-order" rather than "Sold out", because it is a temporary supply
 *             decision, not a dead end.
 */
(function () {
  "use strict";

  var FAMILY_MAP = {
    PLA: "PLA",
    PETG: "PETG",
    ABS: "ABS",
    ASA: "ASA",
    TPU: "TPU",
    PC: "PC",
    Nylon: "Nylon",
    "PET-PPS": "PET/PPS",
    Support: "Support",
  };
  var FAMILY_ORDER = [
    "PLA", "PETG", "ABS", "ASA", "TPU", "PC", "Nylon", "PET/PPS", "Support",
  ];
  var LOW_STOCK_THRESHOLD = 5;

  // Pre-order terms. Keep in step with the copy on the pre-order page and with the
  // 85% prices on the twins. Changing the lead time is a one-line edit here.
  var PREORDER_LEAD_TIME = "3–5 weeks";

  var root = document.getElementById("bfs-root");
  if (!root) return;

  var MODE = root.getAttribute("data-bfs-mode") === "preorder" ? "preorder" : "ready";
  var IS_PREORDER = MODE === "preorder";

  // NOTE: the data script tag is a sibling of #bfs-root in the Liquid markup
  // (it sits after the root div closes), so this must not be scoped to root.
  var dataEl = document.querySelector("[data-bfs-data]");
  var app = root.querySelector("[data-bfs-app]");
  var emptyEl = root.querySelector("[data-bfs-empty]");

  var products = [];
  try {
    products = JSON.parse((dataEl && dataEl.textContent) || "[]");
  } catch (e) {
    console.error("Bambu Filament Selector: could not parse product data", e);
    products = [];
  }

  /* STOCK vs AVAILABILITY.
     `available` answers "can this go in a cart", which is not the same question as
     "is there any". On the pre-order twins every variant is tracked with policy DENY
     and seeded to 9999, so 0 means Ken has switched that colour or spool type off.
     Everything that asks about physical stock must go through hasStock(). */
  function hasStock(v) {
    if (!v) return false;
    return typeof v.stockQty === "number" ? v.stockQty > 0 : !!v.available;
  }

  // READY MODE HIDES EMPTY SERIES ENTIRELY. Done here, once, before families are
  // derived — so a family whose every series is out of stock drops out too rather
  // than rendering a pill that leads to an empty picker.
  if (!IS_PREORDER) {
    products = products.filter(function (p) {
      return (p.variants || []).some(hasStock);
    });
  }

  if (!products.length) {
    if (emptyEl) emptyEl.hidden = false;
    return;
  }

  var byFamily = {};
  products.forEach(function (p) {
    var fam = FAMILY_MAP[p.material] || "Other";
    if (!byFamily[fam]) byFamily[fam] = [];
    byFamily[fam].push(p);
  });
  var families = FAMILY_ORDER.filter(function (f) {
    return byFamily[f] && byFamily[f].length;
  });
  if (byFamily["Other"] && byFamily["Other"].length) {
    families.push("Other");
    console.warn(
      "Bambu Filament Selector: " + byFamily["Other"].length +
        " product(s) have no recognised family: tag",
      byFamily["Other"].map(function (p) { return p.handle; })
    );
  }

  if (!families.length) {
    if (emptyEl) emptyEl.hidden = false;
    return;
  }

  app.hidden = false;

  var els = {
    hero: root.querySelector("[data-bfs-hero]"),
    familyPills: root.querySelector("[data-bfs-family-pills]"),
    productPills: root.querySelector("[data-bfs-product-pills]"),
    seriesName: root.querySelector("[data-bfs-series-name]"),
    colourName: root.querySelector("[data-bfs-colour-name]"),
    colourSwatches: root.querySelector("[data-bfs-colour-swatches]"),
    spoolControl: root.querySelector("[data-bfs-spool-control]"),
    spoolPills: root.querySelector("[data-bfs-spool-pills]"),
    price: root.querySelector("[data-bfs-price]"),
    stock: root.querySelector("[data-bfs-stock]"),
    qtyInput: root.querySelector("[data-bfs-qty]"),
    qtyMinus: root.querySelector("[data-bfs-qty-minus]"),
    qtyPlus: root.querySelector("[data-bfs-qty-plus]"),
    qtyNote: root.querySelector("[data-bfs-qty-note]"),
    addToCart: root.querySelector("[data-bfs-add-to-cart]"),
    addFeedback: root.querySelector("[data-bfs-add-feedback]"),
    tabs: root.querySelectorAll("[data-bfs-tab]"),
    panels: {
      description: root.querySelector('[data-bfs-panel="description"]'),
      specs: root.querySelector('[data-bfs-panel="specs"]'),
      downloads: root.querySelector('[data-bfs-panel="downloads"]'),
    },
  };

  // Every quantity control is optional: this JS and the section deploy as separate
  // files, so a cached older section could render without them. Guarded rather than
  // assumed — a missing stepper must not take Add to cart down with it.
  var HAS_QTY = !!(els.qtyInput && els.qtyMinus && els.qtyPlus);

  /* WHERE THE "PRE-ORDER THE REST" LINK COMES FROM.
     Read off the cross-link the section already renders, rather than hardcoding
     /pages/filament-preorder a second time. One source of truth: if Ken repoints the
     cross-link in the theme editor, this follows automatically. Only meaningful in
     ready mode — on the pre-order page the cross-link points back at ready stock,
     which would be the wrong destination for "we don't have enough". */
  var crossLinkEl = root.querySelector(".bfs__crosslink a");
  var PREORDER_URL = !IS_PREORDER && crossLinkEl ? crossLinkEl.getAttribute("href") : "";

  var state = {
    family: null,
    productId: null,
    colourName: null,
    spoolType: null,
    qty: 1,
    qtyCapped: false,
  };

  function getProductsInFamily(fam) { return byFamily[fam] || []; }

  function getProduct(productId) {
    return products.filter(function (p) { return p.productId === productId; })[0];
  }

  function getVariantsForColour(product, colourName) {
    return product.variants.filter(function (v) { return v.colourName === colourName; });
  }

  // Colours the customer should SEE. Ready mode drops anything with no stock;
  // pre-order mode shows everything and marks the switched-off ones instead.
  function getVisibleColours(product) {
    var seen = {}, out = [];
    product.variants.forEach(function (v) {
      if (seen[v.colourName]) return;
      seen[v.colourName] = true;
      out.push(v.colourName);
    });
    if (IS_PREORDER) return out;
    return out.filter(function (c) {
      return getVariantsForColour(product, c).some(hasStock);
    });
  }

  // Spool types for a colour. Ready mode hides a Refill with no stock while keeping
  // its Spool; pre-order mode shows both and dims whichever is switched off. This is
  // the case worth testing — one colour, two spool types, different answers.
  function getVisibleSpoolTypes(product, colourName) {
    var variants = getVariantsForColour(product, colourName);
    var types = [];
    variants.forEach(function (v) {
      if (types.indexOf(v.spoolType) === -1) types.push(v.spoolType);
    });
    if (IS_PREORDER) return types;
    return types.filter(function (t) {
      return variants.some(function (v) { return v.spoolType === t && hasStock(v); });
    });
  }

  function spoolTypeHasStock(product, colourName, spoolType) {
    return getVariantsForColour(product, colourName).some(function (v) {
      return v.spoolType === spoolType && hasStock(v);
    });
  }

  function firstColour(product) {
    var colours = getVisibleColours(product);
    var withStock = colours.filter(function (c) {
      return getVariantsForColour(product, c).some(hasStock);
    });
    return withStock[0] || colours[0];
  }

  function firstSpool(product, colourName) {
    var types = getVisibleSpoolTypes(product, colourName);
    var withStock = types.filter(function (t) {
      return spoolTypeHasStock(product, colourName, t);
    });
    return withStock[0] || types[0];
  }

  function currentVariant() {
    var product = getProduct(state.productId);
    if (!product) return null;
    var variants = getVariantsForColour(product, state.colourName);
    return (
      variants.filter(function (v) { return v.spoolType === state.spoolType; })[0] ||
      variants[0] || null
    );
  }

  /* HOW MANY OF THIS VARIANT A CUSTOMER MAY ADD.
     Ready-stock variants are inventoryPolicy DENY as of Block 5, so anything beyond
     stockQty is refused at checkout — capping here turns a late, confusing rejection
     into an early, explained one. On the twins stockQty is the 9999 switch, so the
     cap is effectively absent, which is correct: a pre-order is not limited by stock.
     Note stockQty is embedded at render time and can be stale; Shopify stays the
     authority, this is a courtesy. */
  function maxQty(variant) {
    if (!variant || !variant.available) return 1;
    var q = variant.stockQty;
    return typeof q === "number" && q > 0 ? q : 1;
  }

  function setQty(n, fromUser) {
    var max = maxQty(currentVariant());
    var want = parseInt(n, 10);
    if (isNaN(want) || want < 1) want = 1;
    state.qtyCapped = !!fromUser && want > max;
    state.qty = Math.min(want, max);
    renderQty();
  }

  function renderQty() {
    if (!HAS_QTY) return;
    var variant = currentVariant();
    var max = maxQty(variant);
    var usable = !!(variant && variant.available);

    if (state.qty > max) state.qty = max;
    if (state.qty < 1) state.qty = 1;

    els.qtyInput.value = state.qty;
    els.qtyInput.setAttribute("max", max);
    els.qtyInput.disabled = !usable;
    els.qtyMinus.disabled = !usable || state.qty <= 1;
    els.qtyPlus.disabled = !usable || state.qty >= max;

    if (!els.qtyNote) return;

    /* WHEN THE NOTE APPEARS.
       First build only showed it if the customer TYPED a number above the cap — so
       clicking + until it went dead showed nothing at all, which is precisely the
       moment that needs explaining (Ken, 2026-09-07: "i don't see this"). It now
       fires on simply reaching the ceiling too.

       The `state.qty > 1` guard keeps it off a variant with a single unit in stock,
       where + is dead from the outset and the stock line above already says "Only 1
       left in stock" — no point saying it twice before they have asked for more. */
    var atCeiling = state.qty >= max && state.qty > 1;
    var note = "";
    if (usable && (state.qtyCapped || atCeiling)) {
      if (IS_PREORDER) {
        note = "Only " + max + " available to pre-order.";
      } else if (PREORDER_URL) {
        note =
          "Only " + max + " in stock — need more? " +
          '<a href="' + PREORDER_URL + '">Pre-order the rest here.</a>';
      } else {
        note = "Only " + max + " in stock.";
      }
    }
    els.qtyNote.innerHTML = note;
    els.qtyNote.hidden = !note;
  }

  function setFamily(fam) {
    state.family = fam;
    var list = getProductsInFamily(fam);
    var product = list[0];
    setProduct(product ? product.productId : null, true);
    renderFamilyPills();
  }

  function setProduct(productId, skipFamilyRender) {
    state.productId = productId;
    var product = getProduct(productId);
    if (product) {
      var colour = firstColour(product);
      state.colourName = colour;
      state.spoolType = firstSpool(product, colour);
    }
    renderProductPills();
    renderColourSwatches();
    renderSpoolPills();
    renderDetails();
    if (!skipFamilyRender) renderFamilyPills();
  }

  function setColour(colourName) {
    state.colourName = colourName;
    var product = getProduct(state.productId);
    state.spoolType = firstSpool(product, colourName);
    renderColourSwatches();
    renderSpoolPills();
    renderDetails();
  }

  function setSpool(spoolType) {
    state.spoolType = spoolType;
    renderSpoolPills();
    renderDetails();
  }

  function makePill(label, isActive, onClick, isUnavailable) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className =
      "bfs__pill" + (isActive ? " is-active" : "") + (isUnavailable ? " is-unavailable" : "");
    btn.textContent = label;
    if (isUnavailable) {
      btn.setAttribute("aria-label", label + " — currently not available for pre-order");
    }
    btn.addEventListener("click", onClick);
    return btn;
  }

  function renderFamilyPills() {
    els.familyPills.innerHTML = "";
    families.forEach(function (fam) {
      els.familyPills.appendChild(
        makePill(fam, fam === state.family, function () {
          if (fam !== state.family) setFamily(fam);
        })
      );
    });
  }

  function renderProductPills() {
    els.productPills.innerHTML = "";
    getProductsInFamily(state.family).forEach(function (p) {
      els.productPills.appendChild(
        makePill(p.seriesName, p.productId === state.productId, function () {
          if (p.productId !== state.productId) setProduct(p.productId);
        })
      );
    });
  }

  function renderColourSwatches() {
    var product = getProduct(state.productId);
    els.colourSwatches.innerHTML = "";
    if (!product) return;
    getVisibleColours(product).forEach(function (colourName) {
      var swatch = product.swatches[colourName] || {};
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "bfs__swatch" + (colourName === state.colourName ? " is-active" : "");
      btn.title = colourName;
      if (swatch.image) {
        btn.style.backgroundImage = "url('" + swatch.image + "')";
      } else {
        btn.style.backgroundColor = swatch.hex || "#CCCCCC";
      }

      // Only pre-order mode can show a colour with nothing behind it — ready mode
      // filtered those out above.
      var anyStock = getVariantsForColour(product, colourName).some(hasStock);
      if (!anyStock) {
        btn.classList.add("is-unavailable");
        btn.setAttribute("aria-label", colourName + " — currently not available for pre-order");
      } else {
        btn.setAttribute("aria-label", colourName);
      }

      btn.addEventListener("click", function () {
        if (colourName !== state.colourName) setColour(colourName);
      });
      els.colourSwatches.appendChild(btn);
    });
    els.colourName.textContent = state.colourName ? "— " + state.colourName : "";
  }

  function renderSpoolPills() {
    var product = getProduct(state.productId);
    if (!product) return;
    var types = getVisibleSpoolTypes(product, state.colourName);
    // Show the control when there is a genuine choice (Spool/Refill, or 500g/1KG), and
    // also when there is only one option but it names a size — PPA-CF, PPS-CF and the
    // supports each have a single "750g Spool" / "500g Spool", worth stating. Hide it
    // only for the generic single "Spool" fallback, which carries no information.
    var showSpool =
      types.length >= 2 || (types.length === 1 && types[0] && types[0] !== "Spool");
    if (!showSpool) {
      els.spoolControl.hidden = true;
      els.spoolPills.innerHTML = "";
      return;
    }
    els.spoolControl.hidden = false;
    els.spoolPills.innerHTML = "";
    types.forEach(function (type) {
      var unavailable = !spoolTypeHasStock(product, state.colourName, type);
      els.spoolPills.appendChild(
        makePill(type, type === state.spoolType, function () {
          if (type !== state.spoolType) setSpool(type);
        }, unavailable)
      );
    });
  }

  function renderDetails() {
    var product = getProduct(state.productId);
    if (!product) return;
    var variant = currentVariant();

    els.seriesName.textContent = product.seriesName;

    if (variant && variant.image) {
      els.hero.src = variant.image;
      els.hero.alt = product.seriesName + " — " + state.colourName;
    }

    if (variant) {
      var priceHtml = variant.priceFormatted || "";
      if (
        variant.compareAtPrice &&
        parseFloat(variant.compareAtPrice) > parseFloat(variant.price)
      ) {
        /* USE THE FORMATTED STRING, NOT THE RAW FIELD. `compareAtPrice` is cents
           straight off Liquid's v.compare_at_price — concatenating it rendered a bare
           "2000" next to "$17.00" on the pre-order page (Ken's preview, 2026-09-06).
           Never showed before because no ready-stock variant carries a compare-at
           price; every pre-order twin does, by design. The fallback keeps the old
           behaviour if the section is ever served from a cached older render. */
        priceHtml =
          '<s class="bfs__compare-price">' +
          (variant.compareAtPriceFormatted || variant.compareAtPrice) +
          "</s> " +
          priceHtml;
      }
      els.price.innerHTML = priceHtml;
    } else {
      els.price.textContent = "";
    }

    renderStock(variant);
    renderAddToCart(variant);

    // Changing variant clears any "only N in stock" note: it referred to the previous
    // variant's ceiling. renderQty then re-clamps against the new one.
    state.qtyCapped = false;
    renderQty();

    els.panels.description.innerHTML = product.descriptionHtml || "";
    els.panels.specs.innerHTML =
      product.specificationsHtml || "<p>No specifications available.</p>";
    renderDownloads(product.downloads || []);
  }

  function renderStock(variant) {
    els.stock.innerHTML = "";
    if (!variant) return;
    var qty = variant.stockQty;

    if (IS_PREORDER) {
      if (!hasStock(variant)) {
        els.stock.innerHTML =
          '<p class="bfs__stock-line bfs__stock-line--soldout">' +
          '<span class="bfs__stock-dot"></span>Currently not available for pre-order.</p>';
      } else {
        els.stock.innerHTML =
          '<p class="bfs__stock-line bfs__stock-line--preorder">' +
          '<span class="bfs__stock-dot"></span>Pre-order — ships in ' +
          PREORDER_LEAD_TIME +
          ". Price already includes 15% off.</p>";
      }
      return;
    }

    if (typeof qty === "number" && qty > 0 && qty < LOW_STOCK_THRESHOLD) {
      els.stock.innerHTML =
        '<p class="bfs__stock-line bfs__stock-line--low">' +
        '<span class="bfs__stock-dot"></span>Only ' + qty + " left in stock.</p>";
    } else if (!variant.available) {
      els.stock.innerHTML =
        '<p class="bfs__stock-line bfs__stock-line--soldout">Sold out.</p>';
    }
  }

  function renderAddToCart(variant) {
    if (!variant) {
      els.addToCart.disabled = true;
      els.addToCart.textContent = "Unavailable";
      return;
    }
    els.addToCart.disabled = !variant.available;
    if (!variant.available) {
      els.addToCart.textContent = IS_PREORDER ? "Not available" : "Sold out";
    } else {
      els.addToCart.textContent = IS_PREORDER ? "Pre-order" : "Add to cart";
    }
    // variant.variantId is already the plain numeric ID (Liquid's v.id, not a GID string).
    els.addToCart.dataset.variantId = variant.variantId || "";
  }

  function renderDownloads(downloads) {
    if (!downloads.length) {
      els.panels.downloads.innerHTML = "<p>No downloads available for this series.</p>";
      return;
    }
    var html = '<div class="bfs__downloads-grid">';
    downloads.forEach(function (doc) {
      html +=
        '<a class="bfs__download-link" href="' + doc.url +
        '" target="_blank" rel="noopener">' +
        '<span class="bfs__download-badge">PDF</span><span>' + doc.label + "</span></a>";
    });
    html += "</div>";
    els.panels.downloads.innerHTML = html;
  }

  function bindTabs() {
    Array.prototype.forEach.call(els.tabs, function (tab) {
      tab.addEventListener("click", function () {
        Array.prototype.forEach.call(els.tabs, function (t) {
          t.classList.remove("is-active");
        });
        tab.classList.add("is-active");
        Object.keys(els.panels).forEach(function (key) {
          els.panels[key].hidden = key !== tab.dataset.bfsTab;
        });
      });
    });
  }

  function bindQty() {
    if (!HAS_QTY) return;
    els.qtyMinus.addEventListener("click", function () { setQty(state.qty - 1, false); });
    els.qtyPlus.addEventListener("click", function () { setQty(state.qty + 1, true); });
    /* Clamped on `change`/`blur`, NOT on `input`. Rewriting the field mid-keystroke
       fights the typist — typing "10" against a max of 3 would snap to 3 after the
       first character and swallow the second. Clicking Add to cart blurs the field
       first, so a typed value is always committed before the add. */
    els.qtyInput.addEventListener("change", function () { setQty(els.qtyInput.value, true); });
    els.qtyInput.addEventListener("blur", function () { setQty(els.qtyInput.value, true); });
  }

  function bindAddToCart() {
    els.addToCart.addEventListener("click", function () {
      var variant = currentVariant();
      if (!variant || !variant.available) return;
      var numericId = variant.variantId;
      var qty = HAS_QTY ? state.qty : 1;
      els.addToCart.disabled = true;
      els.addToCart.textContent = "Adding…";
      els.addFeedback.textContent = "";

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: numericId, quantity: qty }),
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        })
        .then(function () {
          els.addToCart.textContent = "Added ✓";
          var many = qty > 1 ? qty + " × " : "";
          els.addFeedback.textContent = IS_PREORDER
            ? "Added " + many + "as a pre-order — ships in " + PREORDER_LEAD_TIME + "."
            : "Added " + many + "to cart.";
          updateCartCountBadge();
          // Back to 1 so a second click does not silently repeat the whole quantity.
          setQty(1, false);
          setTimeout(function () { renderAddToCart(currentVariant()); }, 1600);
        })
        .catch(function (err) {
          els.addToCart.textContent = IS_PREORDER ? "Pre-order" : "Add to cart";
          els.addToCart.disabled = false;
          els.addFeedback.textContent =
            (err && err.description) || "Could not add to cart. Please try again.";
        });
    });
  }

  function updateCartCountBadge() {
    fetch("/cart.js")
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll("[data-cart-count], .cart-count").forEach(function (el) {
          el.textContent = cart.item_count;
        });
        document.dispatchEvent(new CustomEvent("cart:add", { detail: { cart: cart } }));
      })
      .catch(function () {
        /* non-fatal: item is in the cart even if the badge doesn't refresh */
      });
  }

  bindTabs();
  bindQty();
  bindAddToCart();
  setFamily(families[0]);
})();
